/**
 * i18n runtime: the i18next instance, namespaces, and the boot/switch helpers.
 * Locale detection + persistence live in ./locale (pure, testable).
 *
 * Catalogs are JSON-per-namespace under ./locales/<locale>/<ns>.json. Only `en`
 * exists today — extraction (Phases 3–5) fills it; translation (Phases 7–8)
 * adds es-419/pt-BR/fr. `fallbackLng: 'en'` means a missing key renders English,
 * never a raw key. A dev-only pseudo-locale (en-XA) is derived from `en`.
 */
import i18n, { type Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import enCommon from './locales/en/common.json';
import enCountry from './locales/en/country.json';
import enEvents from './locales/en/events.json';
import enFollow from './locales/en/follow.json';
import enHome from './locales/en/home.json';
import enMap from './locales/en/map.json';
import enMatch from './locales/en/match.json';
import enOnboarding from './locales/en/onboarding.json';
import enSaved from './locales/en/saved.json';
import enSchedule from './locales/en/schedule.json';
import enWatchparty from './locales/en/watchparty.json';
import esCommon from './locales/es-419/common.json';
import esCountry from './locales/es-419/country.json';
import esEvents from './locales/es-419/events.json';
import esFollow from './locales/es-419/follow.json';
import esHome from './locales/es-419/home.json';
import esMap from './locales/es-419/map.json';
import esMatch from './locales/es-419/match.json';
import esOnboarding from './locales/es-419/onboarding.json';
import esSaved from './locales/es-419/saved.json';
import esSchedule from './locales/es-419/schedule.json';
import esWatchparty from './locales/es-419/watchparty.json';
import frCommon from './locales/fr/common.json';
import frCountry from './locales/fr/country.json';
import frEvents from './locales/fr/events.json';
import frFollow from './locales/fr/follow.json';
import frHome from './locales/fr/home.json';
import frMap from './locales/fr/map.json';
import frMatch from './locales/fr/match.json';
import frOnboarding from './locales/fr/onboarding.json';
import frSaved from './locales/fr/saved.json';
import frSchedule from './locales/fr/schedule.json';
import frWatchparty from './locales/fr/watchparty.json';
import ptCommon from './locales/pt-BR/common.json';
import ptCountry from './locales/pt-BR/country.json';
import ptEvents from './locales/pt-BR/events.json';
import ptFollow from './locales/pt-BR/follow.json';
import ptHome from './locales/pt-BR/home.json';
import ptMap from './locales/pt-BR/map.json';
import ptMatch from './locales/pt-BR/match.json';
import ptOnboarding from './locales/pt-BR/onboarding.json';
import ptSaved from './locales/pt-BR/saved.json';
import ptSchedule from './locales/pt-BR/schedule.json';
import ptWatchparty from './locales/pt-BR/watchparty.json';
import {
  detectInitialLocale,
  DEFAULT_LOCALE,
  type Locale,
  persistLocale,
  PSEUDO_LOCALE,
  pseudoize,
  SUPPORTED_LOCALES,
} from './locale';

export const NAMESPACES = [
  'common',
  'home',
  'events',
  'saved',
  'follow',
  'match',
  'onboarding',
  'schedule',
  'country',
  'watchparty',
  'map',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

const en = {
  common: enCommon,
  home: enHome,
  events: enEvents,
  saved: enSaved,
  follow: enFollow,
  match: enMatch,
  onboarding: enOnboarding,
  schedule: enSchedule,
  country: enCountry,
  watchparty: enWatchparty,
  map: enMap,
};

const es419 = {
  common: esCommon,
  home: esHome,
  events: esEvents,
  saved: esSaved,
  follow: esFollow,
  match: esMatch,
  onboarding: esOnboarding,
  schedule: esSchedule,
  country: esCountry,
  watchparty: esWatchparty,
  map: esMap,
};

const fr = {
  common: frCommon,
  home: frHome,
  events: frEvents,
  saved: frSaved,
  follow: frFollow,
  match: frMatch,
  onboarding: frOnboarding,
  schedule: frSchedule,
  country: frCountry,
  watchparty: frWatchparty,
  map: frMap,
};

const ptBR = {
  common: ptCommon,
  home: ptHome,
  events: ptEvents,
  saved: ptSaved,
  follow: ptFollow,
  match: ptMatch,
  onboarding: ptOnboarding,
  schedule: ptSchedule,
  country: ptCountry,
  watchparty: ptWatchparty,
  map: ptMap,
};

/** Recursively pseudoize every string value in a namespace tree. */
function deepPseudo(node: unknown): unknown {
  if (typeof node === 'string') return pseudoize(node);
  if (Array.isArray(node)) return node.map(deepPseudo);
  if (node && typeof node === 'object') {
    return Object.fromEntries(Object.entries(node).map(([k, v]) => [k, deepPseudo(v)]));
  }
  return node;
}

const resources: Resource = { en, 'es-419': es419, 'pt-BR': ptBR, fr };

// Dev-only: register the pseudo-locale derived from the en catalog.
if (import.meta.env.DEV) {
  resources[PSEUDO_LOCALE] = deepPseudo(en) as Resource[string];
}

void i18n.use(initReactI18next).init({
  resources,
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: [...SUPPORTED_LOCALES, ...(import.meta.env.DEV ? [PSEUDO_LOCALE] : [])],
  ns: NAMESPACES as unknown as string[],
  defaultNS: 'common',
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
});

/**
 * Resolve and apply the boot locale (stored override → device → default).
 * Fire-and-forget from app entry; the app renders in `en` until this resolves,
 * which is invisible while only `en` has translations.
 */
export async function initI18nLocale(): Promise<Locale> {
  const locale = await detectInitialLocale();
  if (locale !== i18n.language) await i18n.changeLanguage(locale);
  return locale;
}

/** Switch language from the UI: persist the choice, then apply it live. */
export async function setLocale(locale: Locale): Promise<void> {
  await persistLocale(locale);
  await i18n.changeLanguage(locale);
}

export default i18n;
